import { AnimationDuration } from "../constants/animation_duration.js";
import { Axis } from "../constants/axis.js";
import { Direction } from "../constants/direction.js";
import { PageUrls } from "../constants/page_urls.js";
import { ApiError } from "../errors/ApiError.js";
import { createCustomEvent } from "../events/custom_event.js";
import { FeedEvent } from "../events/feed_event.js";
import { PanEvent } from "../events/pan_event.js";
import { PanGesture } from "../mixins/pan_gesture.js";
import { ParsedCallback } from "../mixins/parsed_callback.js";
import { UseMixins } from "../mixins/use_mixins.js";
import { throttle } from "../utilities/debounce.js";
import {
  createElement,
  getElement,
  getElements,
} from "../utilities/elements.js";
import { upcaseFirst } from "../utilities/strings.js";
import {
  setTranslate,
  setTranslateX,
  setTranslateY,
  translateValueForCoordinates,
} from "../utilities/transform.js";
import { DickTokComponent } from "./Component.js";
import { defineElement } from "./registry.js";

/**
 * @typedef {import('../types/pan_gesture.js').PanEventDetail} PanEventDetail
 * @typedef {import('../types/pan_gesture.js').PanGestureInterface} PanGestureInterface
 * @typedef {import('../types/types.js').Coordinates} Coordinates
 * @typedef {import('../types/types.js').EventType} EventType
 * @typedef {import('../types/types.js').Listener} Listener
 * @typedef {import('../types/types.js').UsingMixin<DickTokComponent, PanGestureInterface>} UsingPanGesture
 */

/** @type {number} Width of .dicktok-feed--tabs--tab-indicator element in px */
const TAB_INDICATOR_WIDTH_PX = 30;

/** @type {number} Number of items ahead to look when preloading. */
const NUM_AHEAD_TO_LOAD = 2;

/** @type {number} */
const MIN_SWIPE_VELOCITY_PX_MS = 0.3;

/** @type {number} Min distance that a user can peek before snapping to the next post */
const PEEK_THRESHOLD_RATIO_VERTICAL = 0.2;

/** @type {number} Min distance that a user can peek before snapping to the next feed */
const PEEK_THRESHOLD_RATIO_HORIZONTAL = 0.333;

/** @enum {string} */
const CssClass = Object.freeze({
  ACTIVE_TAB: "dicktok-feed__tab--active",
  EASE_OUT: "dicktok-feed--scroll-container--ease-out",
});

export class DickTokFeed extends UseMixins(DickTokComponent, [
  ParsedCallback,
  PanGesture,
]) {
  static services = ["DeviceService", "LoggingService", "PostApiService"];

  static subscriptions = ["FeedEvents"];

  static attributes = [
    { name: "feed", type: String, optional: true },
    { name: "prev", type: String, optional: true },
    { name: "next", type: String },
  ];

  static targets = ["container", "tabs"];

  /** @type {Map<number, string>} Tab index to feed name */
  #tabIndexToFeedName = new Map();
  /** @type {Map<string, number>} Feed name to tab index */
  #feedNameToTabIndex = new Map();
  /** @type {Map<number, number>} Tab index to tab position */
  #tabIndexToPosition = new Map();
  /** @type {HTMLElement} Active tab indicator target */
  #activeTabIndicatorTarget;

  /** @type {number} Current feed item index */
  #feedItemIndex = 0;

  /** @type {boolean} If feed items are loading */
  #loading = false;
  /** @type {boolean} If there are more feed items to load in the "prev" direction. */
  hasMorePrev = false;
  /** @type {boolean} If there are more feed items to load in the "next" direction. */
  hasMoreNext = false;

  /** @type {number} Element height in px. */
  #height = 0;
  /** @type {number} Element width in px. */
  #width = 0;

  /** @type {number} */
  #containerTopPx = 0;

  connectedCallback() {
    super.connectedCallback();
  }

  parsedCallback() {
    this.#updateDimensions();
    this.#setupTabs();
    this.#setupFeed();
    this.#setupPagination();
    this.#setupListeners();
    this.#setupMobileListeners();
    this.#setupDesktopListeners();
  }

  /**
   * Update the element dimension properties and CSS variables.
   */
  #updateDimensions() {
    this.#height = this.offsetHeight;
    this.#width = this.offsetWidth;
    // Feed items use this variable to set their height using CSS variable
    // lets this be set independently of the browser viewport
    this.style.setProperty("--dicktok-feed-height", `${this.#height}px`);
    this.style.setProperty("--dicktok-feed-width", `${this.#width}px`);
  }

  #updateFeedPosition() {
    // Update virtual "scroll" position on the feed container
    this.#containerTopPx = this.#feedItemIndex * this.#height * -1;
    // Use `translate` instead of `marginTop` to force hardware acceleration
    setTranslate(this.containerTarget, { x: 0, y: this.#containerTopPx });
  }

  #resetFeedPosition() {
    this.#feedItemIndex = 0;
    // Update scroll position
    this.#containerTopPx = 0;
    setTranslate(this.containerTarget, { x: 0, y: 0 });
  }

  #setupTabs() {
    if (!this.hasTabsTarget) {
      this.disablePanning(Axis.HORIZONTAL);
      return;
    }

    this.#activeTabIndicatorTarget = createTabIndicator(this.tabsTarget);

    // Setup click handlers for tabs
    const tabElements = getElements(this.tabsTarget, "[data-tab-index]");
    tabElements.forEach((tabElement) => {
      const tabIndex = Number(tabElement.dataset.tabIndex);
      // Click also covers touch events
      tabElement.addEventListener(
        "click",
        () => {
          this.updateCurrentTab(tabIndex);
        },
        { signal: this.disconnectSignal },
      );
    });

    // Read the tag positions and move active tab indicator to active tab
    this.#measureTabs();
  }

  #measureTabs() {
    if (!this.hasTabsTarget) {
      return;
    }

    const tabElements = getElements(this.tabsTarget, "[data-tab-index]");

    // getBoundingClientRect() requires a layout?, and doing a layout during an event
    // handler would have a very bad impact on performance. Tabs don't move, so cache
    // the x coordinates for each tab on initialization
    tabElements.forEach((tabElement) => {
      const tabIndex = Number(tabElement.dataset.tabIndex);
      const feed = tabElement.dataset.feed;
      this.#tabIndexToFeedName.set(tabIndex, feed);
      this.#feedNameToTabIndex.set(feed, tabIndex);
      const { x: tabX, width: tabWidth } = tabElement.getBoundingClientRect();
      const position = tabX + tabWidth / 2 - TAB_INDICATOR_WIDTH_PX / 2;
      this.#tabIndexToPosition.set(tabIndex, position);
    });

    setTranslateX(
      this.#activeTabIndicatorTarget,
      this.#tabIndexToPosition.get(this.#currentTabIndex),
    );
  }

  #setupFeed() {
    // This needs get called after `#setupTabs()` since it reads the feeds that
    // that `#currentTabIndex` uses off the tab element dataset
    if (this.hasFeed && this.#currentTabIndex === -1) {
      throw new Error("Invalid feed");
    }

    this.containerTarget.style.display = "block";
    this.containerTarget.style.position = "relative";
    // Hint to browser that this will be animated to force hardware acceleration
    this.containerTarget.style.willChange = "transform";
  }

  /** Read pagination tokens to initialize hasMorePrev and hasMoreNext */
  #setupPagination() {
    this.hasMorePrev = this.hasPrev;
    this.hasMoreNext = true;
  }

  /**
   * Update the feed item and snap to it. This can be called even
   * if the feed item index has not changed to "snap" back (at the end of a
   * vertical swipe).
   *
   * @param {number} index
   */
  updateFeedItemIndex(index) {
    // Set up snap animation
    this.containerTarget.classList.add(CssClass.EASE_OUT);

    if (index < 0) {
      this.#loadPrevFeedItems();
    } else if (index > this.#feedItemsCount) {
      // stop scrolling at the end
    } else {
      // update index
      this.#feedItemIndex = index;
      if (index - NUM_AHEAD_TO_LOAD <= 0) {
        this.#loadPrevFeedItems();
      }
      if (index + NUM_AHEAD_TO_LOAD >= this.#feedItemsCount) {
        this.#loadNextFeedItems();
      }
    }
    this.#updateFeedPosition();

    // Teardown snap animation
    setTimeout(() => {
      this.containerTarget.classList.remove(CssClass.EASE_OUT);
    }, AnimationDuration.LONG);
  }

  /**
   * Update the tab and snap to it. This can be called even if the
   * tab index has not changed to "snap" back (at the end of a horizontal
   * swipe).
   *
   * @param {number} nextTabIndex
   */
  updateCurrentTab(nextTabIndex) {
    // Prepare snap animation
    this.containerTarget.classList.add(CssClass.EASE_OUT);

    // Snap the tab indicator, this happens in a promise so it actually
    // comes after all the following code, but we want to setup the promise
    // to reference it if the feed changed. However this still executes
    // even if the feed did not change to snap back to the original position.
    const tabIndicatorPosition = this.#tabIndexToPosition.get(nextTabIndex);
    const tabIndicatorAnimation = this.#activeTabIndicatorTarget.animate(
      [
        {
          transform: translateValueForCoordinates({
            x: tabIndicatorPosition,
            y: 0,
          }),
        },
      ],
      { easing: "ease-in", duration: AnimationDuration.SHORT },
    );
    tabIndicatorAnimation.finished.then(() => {
      setTranslateX(this.#activeTabIndicatorTarget, tabIndicatorPosition);
    });

    const nextFeed = this.#tabIndexToFeedName.get(nextTabIndex);
    if (!nextFeed) {
      throw new Error("Invalid feed");
    }

    if (nextFeed !== this.feed) {
      // Update URL
      history.pushState({}, "", `/feeds/${nextFeed}`);
      // Update document Title. It would be better to read from the title
      // attribute on the tab element, but all the feed names are one word and
      // this is faster for now.
      document.title = upcaseFirst(nextFeed);

      this.#loadNewFeed(nextFeed);

      this.#resetFeedPosition();

      // Remove the active tab CSS class from the previous tab
      const previousTab = getElement(
        this.tabsTarget,
        "." + CssClass.ACTIVE_TAB,
      );
      previousTab.classList.remove(CssClass.ACTIVE_TAB);

      tabIndicatorAnimation.finished.then(() => {
        // Add the active tab CSS class to the new tab
        const nextTab = getElement(
          this.tabsTarget,
          `[data-tab-index="${nextTabIndex}"]`,
        );
        nextTab.classList.add(CssClass.ACTIVE_TAB);
      });
    }

    setTranslateX(this.containerTarget, 0);

    // Teardown snap animation
    setTimeout(() => {
      this.containerTarget.classList.remove(CssClass.EASE_OUT);
    }, AnimationDuration.LONG);
  }

  /**
   * Check if loading, and if loading, return. Otherwise, set loading to true,
   * call and await the wrapped function, and then set loading to false.
   *
   * @param {() => Promise<unknown>} wrapped
   */
  async #withLoading(wrapped) {
    if (this.#loading) {
      return;
    }
    this.#loading = true;
    try {
      await wrapped();
    } catch (error) {
      if (error instanceof ApiError && error.statusCode === 401) {
        window.location.href = PageUrls.LOGIN;
      } else {
        this.error(error);
      }
    } finally {
      this.#loading = false;
    }
  }

  /** @param {string} feed */
  async #loadNewFeed(feed) {
    this.#withLoading(async () => {
      this.containerTarget.replaceChildren();
      const { items, prev, next } = await this.postApiService.getFeed(
        feed,
        "feeditem",
      );
      this.prev = prev;
      this.next = next;
      this.feed = feed;
      this.hasMorePrev = Boolean(prev);
      this.hasMoreNext = Boolean(next);
      this.containerTarget.append(...items);
    });
  }

  async #loadPrevFeedItems() {
    this.#withLoading(async () => {
      if (!this.hasMorePrev) {
        return;
      }
      const { items, prev } = await this.postApiService.getPosts(
        this.prev,
        "feeditem",
      );
      this.prev = prev;
      // Need to grab this before prepending the items, because that moves the
      // items out of the HTMLCollection and then the length is 0
      const itemsCount = items.length;
      this.hasMorePrev = itemsCount > 0;
      this.containerTarget.prepend(...items);
      // Everything is shifted down when we add the new items, so we need
      // to update the feed item index to keep the same item in view.
      this.#feedItemIndex = this.#feedItemIndex + itemsCount;
      this.#updateFeedPosition();
    });
  }

  async #loadNextFeedItems() {
    this.#withLoading(async () => {
      if (!this.hasMoreNext) {
        return;
      }
      const { items, next } = await this.postApiService.getPosts(
        this.next,
        "feeditem",
      );
      // Need to grab this before prepending the items, because that moves the
      // items out of the HTMLCollection and then the length is 0
      this.hasMoreNext = items.length > 0;
      this.containerTarget.append(...items);
      this.next = next;
    });
  }

  #setupListeners() {
    // Pass this to automatically remove event listeners on disconnect
    const options = { signal: this.disconnectSignal };

    this.feedEvents.addEventListener(
      FeedEvent.DISABLE_FEED_SCROLLING,
      () => this.disablePanning(Axis.ALL),
      options,
    );
    this.feedEvents.addEventListener(
      FeedEvent.ENABLE_FEED_SCROLLING,
      () =>
        this.hasFeed
          ? this.enablePanning(Axis.ALL)
          : this.enablePanning(Axis.VERTICAL),
      options,
    );

    this.addEventListener(
      PanEvent.PAN_START,
      (event) => {
        this.feedEvents.dispatchEvent(
          createCustomEvent(FeedEvent.FEED_PAN_START, undefined, event.detail),
        );
      },
      options,
    );
    this.addEventListener(PanEvent.PAN_MOVE, this.handlePanMove, options);
    this.addEventListener(PanEvent.PAN_END, this.handlePanEnd, options);
    this.addEventListener(PanEvent.PAN_CANCEL, this.handlePanCancel, options);
  }

  #setupMobileListeners() {
    if (!this.deviceService.mobile) {
      return;
    }

    // Need to use position fixed because the document body will be taller
    this.style.position = "fixed";
    this.style.top = "0";
    document.documentElement.style.height = "200dvh";

    // Not sure if this really happens, but if this is the case, already
    // maximized. It happens in the desktop browser on mobile emulation mode.
    if (window.innerHeight === screen.height) {
      return;
    }

    // Disable pan gestures until the user pans down to hide the address bar
    this.disablePanning(Axis.VERTICAL);

    const options = { signal: this.disconnectSignal };
    const debounceDelayMs = 25;

    // Store the window height to reference in event handlers
    const originalWindowHeight = window.innerHeight;
    let previousWindowHeight = window.innerHeight;

    // Listen to scroll events to measure if the address bar is collapsed
    const measureScroll = throttle(
      /** @param {Event} event */ (event) => {
        // Window is expanding
        if (window.innerHeight > previousWindowHeight) {
          window.scrollTo(0, 1);
          this.#updateDimensions();
          this.#measureTabs();
          this.#updateFeedPosition();
          this.enablePanning(Axis.VERTICAL);
          this.disableEventCapturing();
        }
        previousWindowHeight = window.innerHeight;
      },
      debounceDelayMs,
      /** immediate= */ false,
    );
    window.addEventListener("scroll", measureScroll, options);

    // This is called whenever the visual viewport changes, which unlike
    // the window resize event includes the address bar, so we can listen
    // to this to know when it has been expanded
    const measureResize = throttle(
      /** @param {Event} event */ (event) => {
        if (window.innerHeight < previousWindowHeight) {
          window.scrollTo(0, 1);
          // Since the address bar is now visible again, reset this so
          // the scroll handler can start waiting for it to collapse
          previousWindowHeight = originalWindowHeight;
        }
      },
      /** delayMs= */ 25,
      /** immediate= */ false,
    );
    visualViewport.addEventListener("resize", measureResize, options);

    // Check the state when the window is reopened, as the address bar is
    // usually expanded without firing an event on the visual viewport
    const measureVisibilityChange = throttle(
      /** @param {Event} event */ (event) => {
        if (document.visibilityState === "hidden") {
          return;
        }
        // This triggers a layout so the dimensions are correct
        window.scrollTo(0, 1);
        this.#updateDimensions();
        this.#measureTabs();
        this.#updateFeedPosition();
        this.disablePanning(Axis.VERTICAL);
        previousWindowHeight = window.innerHeight;
      },
      /** delayMs= */ 100,
      /** immediate= */ false,
    );
    document.addEventListener(
      "visibilitychange",
      measureVisibilityChange,
      options,
    );
  }

  #setupDesktopListeners() {
    // Listen to the size of the browser window updating so we can resize
    window.addEventListener(
      "resize",
      (event) => {
        this.#updateDimensions();
        this.#measureTabs();
      },
      {
        signal: this.disconnectSignal,
        passive: true,
      },
    );

    // Mouse event for wheel, also two-touch trackpad gesture for
    // desktop Chrome, Edge and Firefox, see:
    // https://danburzo.ro/dom-gestures/
    this.addEventListener("wheel", this.#handleWheel, {
      signal: this.disconnectSignal,
      passive: true,
    });

    // Keyboard events - arrow keys navigate through the feed
    document.addEventListener("keydown", this.#handleKeyDown, {
      signal: this.disconnectSignal,
    });
  }

  /** @param {CustomEvent<PanEventDetail>} event */
  handlePanMove({ detail }) {
    detail.axis === Axis.HORIZONTAL
      ? this.#handleHorizontalPanMove(detail)
      : this.#handleVerticalPanMove(detail);
  }

  /** @param {PanEventDetail} detail */
  #handleHorizontalPanMove(detail) {
    const nextTabIndex = this.#currentTabIndex + detail.direction;
    // Check there is a feed in the direction of the pan
    if (!this.#tabIndexToFeedName.get(nextTabIndex)) {
      return;
    }
    const feedContainerLeftPx =
      detail.delta.native.x > 0
        ? Math.min(detail.delta.native.x, this.#width)
        : Math.max(detail.delta.native.x, this.#width * -1);
    // Use translate instead of margin to force hardware acceleration
    setTranslateX(this.containerTarget, feedContainerLeftPx);
    // Scroll the tab indicator
    const tabStartLeftPx = this.#tabIndexToPosition.get(this.#currentTabIndex);
    const tabDistancePx = Math.abs(
      this.#tabIndexToPosition.get(nextTabIndex) - tabStartLeftPx,
    );
    const tabDeltaPx = detail.delta.normalized.x * tabDistancePx;
    // Use translate instead of margin to force hardware acceleration
    setTranslateX(this.#activeTabIndicatorTarget, tabStartLeftPx - tabDeltaPx);
  }

  /** @param {PanEventDetail} detail */
  #handleVerticalPanMove(detail) {
    // Check there are more posts to show
    if (this.#feedItemIndex + detail.direction * -1 === this.#feedItemsCount) {
      return;
    }
    const feedContainerDeltaYPx =
      detail.delta.native.y > 0
        ? Math.min(detail.delta.native.y, this.#height)
        : Math.max(detail.delta.native.y, this.#height * -1);
    setTranslateY(
      this.containerTarget,
      this.#containerTopPx + feedContainerDeltaYPx,
    );
  }

  /** @param {CustomEvent<PanEventDetail>} event */
  handlePanEnd({ detail }) {
    detail.axis === Axis.HORIZONTAL
      ? this.#handleHorizontalPanEnd(detail)
      : this.#handleVerticalPanEnd(detail);
    this.feedEvents.dispatchEvent(createCustomEvent(FeedEvent.FEED_PAN_END));
  }

  /** @param {PanEventDetail} detail */
  #handleHorizontalPanEnd(detail) {
    const nextTabIndex = this.#currentTabIndex + detail.direction;
    const shouldUpdateFeed =
      (detail.velocity.x > MIN_SWIPE_VELOCITY_PX_MS ||
        detail.distance.normalized.x > PEEK_THRESHOLD_RATIO_HORIZONTAL) &&
      // Check there is a feed in the direction of the pan
      this.#tabIndexToFeedName.get(nextTabIndex);
    if (shouldUpdateFeed) {
      this.updateCurrentTab(nextTabIndex);
    } else {
      // Triggers the snap-back animation
      this.updateCurrentTab(this.#currentTabIndex);
    }
  }

  /** @param {PanEventDetail} detail */
  #handleVerticalPanEnd(detail) {
    const nextFeedItemIndex = this.#feedItemIndex + detail.direction * -1;
    const shouldUpdateFeedItem =
      (detail.velocity.y > MIN_SWIPE_VELOCITY_PX_MS ||
        detail.distance.normalized.y > PEEK_THRESHOLD_RATIO_VERTICAL) &&
      // Check there are more posts to show
      nextFeedItemIndex < this.#feedItemsCount;
    if (shouldUpdateFeedItem) {
      this.updateFeedItemIndex(nextFeedItemIndex);
    } else {
      // Triggers the snap-back animation
      this.updateFeedItemIndex(this.#feedItemIndex);
    }
  }

  /** @param {CustomEvent} event */
  handlePanCancel(event) {
    // Triggers the snap-back animation
    this.updateFeedItemIndex(this.#feedItemIndex);
    this.feedEvents.dispatchEvent(createCustomEvent(FeedEvent.FEED_PAN_END));
  }

  /** @param {WheelEvent} event */
  #handleWheel = throttle(
    /** @param {WheelEvent} event */
    (event) => {
      // Set by desktop Chrome, Edge and Firefox on simulated wheel event
      // from two-touch trackpad gesture to indicate "pinch and zoom", as
      // opposed to scroll. Don't respond to "pinch and zoom".
      if (event.ctrlKey) {
        return;
      }

      if (!this.panEnabled(Axis.VERTICAL)) {
        return;
      }

      this.containerTarget.classList.add(CssClass.EASE_OUT);

      if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
        const direction = event.deltaY < 0 ? Direction.DOWN : Direction.UP;
        const nextFeedItemIndex = this.#feedItemIndex + direction * -1;
        if (nextFeedItemIndex < this.#feedItemsCount) {
          this.updateFeedItemIndex(nextFeedItemIndex);
        }
      }
    },
    /** delayMs= */ 200,
    /** immediate= */ true,
  );

  /**
   * Handle keydown by updating the current feed item or current feed
   * if the arrow keys were pressed.
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.repeat) {
      return;
    }

    const { key } = event;
    if (["ArrowDown", "ArrowUp", "ArrowRight", "ArrowLeft"].includes(key)) {
      let axis;
      let direction;

      if (key === "ArrowDown" || key === "ArrowUp") {
        axis = Axis.VERTICAL;
        direction = key === "ArrowDown" ? Direction.DOWN : Direction.UP;
      } else {
        axis = Axis.HORIZONTAL;
        direction = key === "ArrowRight" ? Direction.RIGHT : Direction.LEFT;
      }

      if (!this.panEnabled(axis)) {
        return;
      }

      this.containerTarget.classList.add(CssClass.EASE_OUT);

      if (axis === Axis.VERTICAL) {
        const nextFeedItemIndex = this.#feedItemIndex + direction;
        if (nextFeedItemIndex < this.#feedItemsCount) {
          this.updateFeedItemIndex(nextFeedItemIndex);
        }
      } else {
        const nextTabIndex = this.#currentTabIndex + direction * -1;
        this.updateCurrentTab(nextTabIndex);
      }
    }
  };

  get #feedItemsCount() {
    return this.querySelectorAll("dicktok-feeditem").length;
  }

  get #currentTabIndex() {
    return this.#feedNameToTabIndex.get(this.feed) ?? -1;
  }
}

/** @param {HTMLElement} root */
function createTabIndicator(root) {
  return root.appendChild(
    createElement(`
      <div style="
        border-bottom: 2px solid #fff;
        bottom: -4px;
        left: 0;
        position: absolute;
        width: ${TAB_INDICATOR_WIDTH_PX}px;
        will-change: transform;
        z-index: 1;
      "></div>
    `),
  );
}

defineElement("dicktok-feed", DickTokFeed);
